import React from 'react';

/**
 * ChatDAGRAGDAG C2.1 sp3 — the chat-thread fork marker (chevron + count).
 * Lens-agnostic by design: takes data + optional callbacks, never imports
 * LensContext/api, so MessageList stays usable in admin/work chat (no
 * LensProvider) and the unit test stays off the Vite import.meta chain.
 */
export interface ForkBranch {
  turnId: string;       // the Camp / first-spine message id
  joinerName: string;   // persona display name (resolved by the parent)
  task: string;
  personaId: string;
  kind?: 'join' | 'fork';                   // §7.3 — 'fork' = self-fork (same persona); 'join' = invited companion
  forkMode?: 'steer' | 'reroll' | string;   // F2 — read metadata.fork_mode, NOT the reroll prose
}

interface ForkMarkerProps {
  count: number;
  branches: ForkBranch[];
  expanded: boolean;
  onToggle: () => void;
  /** When provided (council surface), selecting a branch drives the lens
   *  write-target. Absent on non-council surfaces → branch rows are
   *  display-only. */
  onFocusBranch?: (b: { personaId: string; turnId: string }) => void;
}

export const ForkMarker: React.FC<ForkMarkerProps> = ({
  count, branches, expanded, onToggle, onFocusBranch,
}) => (
  <div
    data-testid="fork-marker"
    className="my-2 border-y border-dashed border-gray-300 dark:border-gray-600 py-2"
  >
    <button
      data-testid="fork-marker-toggle"
      onClick={onToggle}
      className="flex w-full items-center gap-2 text-left text-sm font-medium text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-gray-100"
    >
      <span aria-hidden className={`transition-transform ${expanded ? 'rotate-90' : ''}`}>▸</span>
      <span aria-hidden>⑂</span>
      <span>{count} trails from here</span>
    </button>
    {branches.map((b) => (
      <button
        key={b.turnId}
        data-testid={`fork-branch-${b.turnId}`}
        onClick={() => onFocusBranch?.({ personaId: b.personaId, turnId: b.turnId })}
        disabled={!onFocusBranch}
        className="mt-1 block w-full pl-6 text-left text-xs text-gray-500 hover:text-gray-800 disabled:cursor-default disabled:hover:text-gray-500 dark:text-gray-400 dark:hover:text-gray-200"
      >
        {b.kind === 'fork'
          ? <>{b.joinerName} · {b.forkMode === 'reroll' ? 're-rolled' : 'new path'}</>
          : <>{b.joinerName} joined · "{b.task}"</>}
      </button>
    ))}
  </div>
);
